import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AuthenticationUrl, RouteTranslationKey } from '../enum';
import { initialHeaderUrl } from '../api/services/settings/settings-api-service';
import { applicationActions } from '../store/application';
import { settingsApiSelectors } from '../store/settings-api';

export const gotoExternalLocation = (location: string, newTab?: boolean) => {
    const a = document.createElement('a');
    a.href = location;
    if (newTab) {
        a.setAttribute('target', '_blank');
    }
    a.click();
    a.remove();
};

export const isExternalUrl = (url: string) => url.includes('.');

export const iamForwardUrl = (baseUrl: AuthenticationUrl, forwardUrl: string, language: string): string =>
    `${baseUrl}?Location=${initialHeaderUrl}${language}${forwardUrl}`;

// the following utils are used to navigate to eBanking
// however, they need to behave very differently dependeing on whether the iFrame integration of the banking portal is enabled or disabled
type EbankingLocation = 'LANDING_PAGE' | 'PRODUCTS' | 'MESSAGES' | 'DOCUMENTS';

const externalLocationsLUT: Record<EbankingLocation, string> = {
    LANDING_PAGE: AuthenticationUrl.EBANKING,
    PRODUCTS: AuthenticationUrl.EBANKING_PRODUCTS,
    MESSAGES: AuthenticationUrl.EBANKING_MAIL,
    DOCUMENTS: AuthenticationUrl.EBANKING_DOCUMENTS,
};

const iFrameLocationsLUT: Record<EbankingLocation, string> = {
    LANDING_PAGE: RouteTranslationKey.EBANKING,
    PRODUCTS: RouteTranslationKey.EBANKING_PRODUCTS,
    MESSAGES: RouteTranslationKey.EBANKING_MESSAGES,
    DOCUMENTS: RouteTranslationKey.EBANKING_DOCUMENTS,
};

export const useEbankingNavigationHandler = (ebankingLocation: EbankingLocation, newTab = true) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const eBankingIFrameEnabled = useSelector(settingsApiSelectors.getEbankingIFrameEnabled);

    return useMemo(
        () =>
            eBankingIFrameEnabled
                ? () => dispatch(applicationActions.changeLocation({ location: t(iFrameLocationsLUT[ebankingLocation]) }))
                : () => gotoExternalLocation(externalLocationsLUT[ebankingLocation], newTab),

        [eBankingIFrameEnabled, ebankingLocation, newTab, dispatch, t],
    );
};
